/**
 * DOG OWNED — Favourites carousel.
 *
 * The track is a native scroll-snap container (see the stylesheet in
 * sections/dog-owned-favourites.liquid), so swiping, trackpad scrolling and
 * tabbing between cards all work with this file absent. It only adds:
 *
 *   - previous/next buttons on larger screens, shown once there is something
 *     to scroll and disabled at either end
 *   - Left/Right arrow keys while the track itself has focus
 *   - the optional autoplay, which is off unless the merchant switches it on,
 *     never runs for visitors who prefer reduced motion, pauses on hover and
 *     focus, and stops for good the moment the customer touches the track
 *
 * It reads no product data and never touches the cart.
 */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  function onMediaChange(query, handler) {
    if (typeof query.addEventListener === 'function') query.addEventListener('change', handler);
    else if (typeof query.addListener === 'function') query.addListener(handler);
  }

  function setupCarousel(root) {
    if (root.hasAttribute('data-favourites-ready')) return;
    root.setAttribute('data-favourites-ready', '');

    var track = root.querySelector('[data-favourites-track]');
    if (!track) return;

    var controls = root.querySelector('[data-favourites-controls]');
    var prev = root.querySelector('[data-favourites-prev]');
    var next = root.querySelector('[data-favourites-next]');

    function behavior() {
      return REDUCED.matches ? 'auto' : 'smooth';
    }

    function step() {
      var slide = track.querySelector('.favourites__slide');
      if (!slide) return track.clientWidth;
      var styles = window.getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return slide.getBoundingClientRect().width + gap;
    }

    function maxScroll() {
      return track.scrollWidth - track.clientWidth;
    }

    function scrollByStep(direction) {
      track.scrollBy({ left: direction * step(), behavior: behavior() });
      // The scroll event refreshes the arrows as the track moves; this settles
      // them once the movement has finished, whatever the browser's timing.
      window.setTimeout(update, 450);
    }

    function update() {
      var canScroll = maxScroll() > 1;
      if (controls) controls.hidden = !canScroll;
      if (prev) prev.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = track.scrollLeft >= maxScroll() - 1;
    }

    if (prev) {
      prev.addEventListener('click', function () {
        scrollByStep(-1);
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        scrollByStep(1);
      });
    }

    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();

    // Arrow keys only when the scroller itself is focused, so they never
    // interfere with a focused card or button inside it.
    track.addEventListener('keydown', function (event) {
      if (event.target !== track) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollByStep(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollByStep(-1);
      }
    });

    /* ------------------------------------------------------------ Autoplay */
    var interval = parseInt(root.getAttribute('data-favourites-autoplay'), 10);
    if (!interval || interval < 2000) return;

    var timer = null;
    var paused = false;
    var stopped = false;

    function tick() {
      if (paused || stopped || document.hidden || REDUCED.matches) return;
      if (track.scrollLeft >= maxScroll() - 1) {
        track.scrollTo({ left: 0, behavior: behavior() });
      } else {
        scrollByStep(1);
      }
    }

    function start() {
      if (stopped || REDUCED.matches || timer) return;
      timer = window.setInterval(tick, interval);
    }

    function stop() {
      if (!timer) return;
      window.clearInterval(timer);
      timer = null;
    }

    function stopForGood() {
      stopped = true;
      stop();
    }

    root.addEventListener('mouseenter', function () {
      paused = true;
    });
    root.addEventListener('mouseleave', function () {
      paused = false;
    });
    root.addEventListener('focusin', function () {
      paused = true;
    });
    root.addEventListener('focusout', function () {
      paused = false;
    });

    // A customer who takes control keeps it.
    track.addEventListener('touchstart', stopForGood, { passive: true });
    track.addEventListener('pointerdown', stopForGood);
    track.addEventListener('wheel', stopForGood, { passive: true });
    if (prev) prev.addEventListener('click', stopForGood);
    if (next) next.addEventListener('click', stopForGood);

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else start();
    });

    onMediaChange(REDUCED, function (event) {
      if (event.matches) stop();
      else start();
    });

    start();
  }

  function init() {
    document.querySelectorAll('[data-favourites]').forEach(setupCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor re-renders sections without a full page load.
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-favourites]');
    if (root) setupCarousel(root);
  });
})();
